import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Table, Button, Input, Row, Col, Badge } from 'reactstrap';
import { Helmet } from 'react-helmet-async';
import AdminLayout from 'Components/Admin/AdminLayout';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function CardsTable() {
  const {
    t
  } = useTranslation();
  const [cards, setCards] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCards, setFilteredCards] = useState([]);
  useEffect(() => {
    loadCards();
    loadProperties();
  }, []);
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = cards.filter(card => card.id?.toLowerCase().includes(searchQuery.toLowerCase()) || card.name?.toLowerCase().includes(searchQuery.toLowerCase()) || card.profileId?.toLowerCase().includes(searchQuery.toLowerCase()));
      setFilteredCards(filtered);
    } else {
      setFilteredCards(cards);
    }
  }, [searchQuery, cards]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/database/tables/Card`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setCards(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading cards:', err);
      setError(`Failed to load cards: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await fetch(`${API_BASE}/database/tables/Property`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProperties(data.data || []);
    } catch (err) {
      console.error('Error loading properties:', err);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getCardProperties = cardId => {
    // For now, we'll show properties that belong to the same profile
    // In a real implementation, you'd need to join Card and Property tables
    const card = cards.find(c => c.id === cardId);
    if (!card) return [];
    return properties.filter(prop => prop.profileId === card.profileId);
  };

  const extractPropertyValue = property => {
    // Try different ways to extract the value from the property
    if (property.value) {
      return property.value;
    }

    if (property.parameters) {
      // Check for numbered parameter structure (e.g., parameters["1"].value)
      if (typeof property.parameters === 'object') {
        // Look for numbered keys like "1", "2", etc. and check their .value property
        for (const key of Object.keys(property.parameters)) {
          if (property.parameters[key] && typeof property.parameters[key] === 'object' && property.parameters[key].value) {
            return property.parameters[key].value;
          }
        } // Check if parameters has a direct value


        if (property.parameters.value) {
          return property.parameters.value;
        } // Check for common parameter structures


        if (property.parameters.email) {
          return property.parameters.email;
        }

        if (property.parameters.phone) {
          return property.parameters.phone;
        }

        if (property.parameters.name) {
          return property.parameters.name;
        } // If no numbered structure, try to find any string value


        const values = Object.values(property.parameters);
        const stringValue = values.find(v => typeof v === 'string' && v.trim() !== '');

        if (stringValue) {
          return stringValue;
        }
      }
    }

    return 'N/A';
  };

  const cardsToShow = filteredCards;
  return <AdminLayout>
            <Helmet>
                <title>{t("Cards Table - Admin - HeresMyInfo.com")}</title>
            </Helmet>

            <div className="container-fluid">
                <Row>
                    <Col>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0">{t("Cards Table")}</h4>
                                <div className="d-flex align-items-center">
                                    <Badge color="info" className="mr-2">
                                        {cardsToShow.length}{t("cards")}</Badge>
                                    <Button color="primary" size="sm" onClick={loadCards} disabled={loading}>
                                        {loading ? 'Loading...' : 'Refresh'}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {error && <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>}

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Input type="text" placeholder={t("Search cards by ID, name, or profileId...")} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{
                    fontWeight: 'bold',
                    color: '#212529',
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #6c757d'
                  }} />
                                    </Col>
                                </Row>

                                {loading ? <div className="text-center py-4">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">{t("Loading...")}</span>
                                        </div>
                                        <p className="mt-2">{t("Loading cards...")}</p>
                                    </div> : <div className="table-responsive">
                                        <Table striped hover>
                                            <thead>
                                                <tr>
                                                    <th>{t("ID")}</th>
                                                    <th>{t("Name")}</th>
                                                    <th>{t("Profile ID")}</th>
                                                    <th>{t("Order")}</th>
                                                    <th>{t("Public")}</th>
                                                    <th>{t("Default Card")}</th>
                                                    <th>{t("Properties")}</th>
                                                    <th>{t("ACL")}</th>
                                                    <th>{t("Created")}</th>
                                                    <th>{t("Updated")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cardsToShow.map((card, index) => <tr key={card.id || index}>
                                                        <td>
                                                            <code className="small">{card.id}</code>
                                                        </td>
                                                        <td>
                                                            <strong>{card.name ? t(card.name) : t('N/A')}</strong>
                                                        </td>
                                                        <td>
                                                            <code className="small">{card.profileId || 'N/A'}</code>
                                                        </td>
                                                        <td>{card.order !== undefined ? card.order : 'N/A'}</td>
                                                        <td>
                                                            <Badge color={card.public ? 'success' : 'secondary'}>
                                                                {card.public ? 'Yes' : 'No'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge color={card.defaultCard ? 'primary' : 'secondary'}>
                                                                {card.defaultCard ? 'Yes' : 'No'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                {getCardProperties(card.id).map((prop, idx) => {
                            const value = extractPropertyValue(prop);
                            const displayValue = value.length > 30 ? value.substring(0, 30) + '...' : value;
                            return <div key={idx} className="mb-1 d-flex align-items-center">
                                                                            <Badge color="info" className="mr-2" style={{
                                minWidth: '80px'
                              }}>{prop.name}</Badge>
                                                                            <small className="text-muted" title={value}>
                                                                                {displayValue}
                                                                            </small>
                                                                        </div>;
                          })}
                                                                {getCardProperties(card.id).length === 0 && <span className="text-muted">{t("No properties")}</span>}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                {(() => {
                            if (!card.acl) return <span className="text-muted">{t("N/A")}</span>;
                            let aclArray = card.acl;

                            if (typeof card.acl === 'string') {
                              try {
                                aclArray = JSON.parse(card.acl);
                              } catch (e) {
                                return <span className="text-danger">{t("Invalid ACL")}</span>;
                              }
                            }

                            if (!Array.isArray(aclArray)) return <span className="text-muted">{t("N/A")}</span>;
                            if (aclArray.length === 0) return <span className="text-muted">{t("Empty")}</span>;
                            return aclArray.map((userId, idx) => <Badge key={idx} color="success" className="mr-1 mb-1">
                                                                            {userId}
                                                                        </Badge>);
                          })()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <small>{formatDate(card.createdAt)}</small>
                                                        </td>
                                                        <td>
                                                            <small>{formatDate(card.updatedAt)}</small>
                                                        </td>
                                                    </tr>)}
                                            </tbody>
                                        </Table>
                                    </div>}

                                {!loading && cardsToShow.length === 0 && <div className="text-center py-4">
                                        <p className="text-muted">
                                            {searchQuery ? 'No cards found matching your search.' : 'No cards found.'}
                                        </p>
                                    </div>}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </AdminLayout>;
}